import { useQuery } from '@tanstack/react-query'
import {
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Info,
  KanbanSquare,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AgileDetailDrawer, type AgileEntity } from '../components/AgileDetailDrawer'
import { AppLayout, PageHeader } from '../components/layout/AppLayout'
import { Badge, taskStatusVariant } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Card, CardBody } from '../components/ui/Card'
import { Empty, Skeleton } from '../components/ui/Empty'
import { Input, Label, Select } from '../components/ui/Input'
import { apiClient } from '../lib/api'

// Tree response includes ALL fields from drizzle .select() — we extend the
// minimum needed for rendering AND open the door to AgileDetailDrawer which
// will read whatever additional fields it needs from the same object via
// AgileEntity widening.

type Task = AgileEntity & {
  id: string
  title: string
  status: string
  priority: string
  assignedAgentType: string | null
  assignedUserId: string | null
}

type UserStory = AgileEntity & {
  id: string
  title: string
  status: string
  tasks: Task[]
}

type Feature = AgileEntity & {
  id: string
  title: string
  description: string | null
  status: string
  priority: string
  userStories: UserStory[]
}

type Tree = {
  features: Feature[]
  counts: { features: number; userStories: number; tasks: number }
}

type Project = { id: string; name: string; clientId: string | null }

type SelectedEntity =
  | { kind: 'feature'; entity: Feature }
  | { kind: 'user_story'; entity: UserStory; feature: Feature }
  | { kind: 'task'; entity: Task; feature: Feature; userStory: UserStory }

// numeric fields from Postgres can come back as strings — coerce safely
function toNum(v: unknown): number {
  if (v == null) return 0
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

function aggregateFeature(f: Feature) {
  let hours = 0
  let sp = 0
  let taskCount = 0
  for (const us of f.userStories ?? []) {
    sp += toNum(us.storyPoints)
    for (const t of us.tasks ?? []) {
      hours += toNum(t.estimatedHours)
      taskCount++
    }
  }
  return { hours, sp, taskCount, usCount: f.userStories?.length ?? 0 }
}

function fmtHours(h: number): string {
  if (h === 0) return ''
  // 0.5 -> "30m", 1 -> "1h", 2.5 -> "2.5h"
  if (h < 1) return `${Math.round(h * 60)}m`
  return Number.isInteger(h) ? `${h}h` : `${h.toFixed(1)}h`
}

const FILTERS_KEY = 'squadron:agile:filters'
const COLLAPSED_KEY = 'squadron:agile:collapsedFeatures'

function loadFilters() {
  try {
    const raw = localStorage.getItem(FILTERS_KEY)
    if (!raw) return null
    return JSON.parse(raw) as { projectId?: string; activeOnly?: boolean; search?: string }
  } catch {
    return null
  }
}

/** Read the persisted collapsed-features set (array of UUIDs → Set). */
function loadCollapsedFeatures(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSED_KEY)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter((x): x is string => typeof x === 'string'))
  } catch {
    return new Set()
  }
}

export function AgilePage() {
  const saved = typeof window !== 'undefined' ? loadFilters() : null
  const [projectId, setProjectId] = useState(saved?.projectId ?? '')
  const [activeOnly, setActiveOnly] = useState(saved?.activeOnly ?? true)
  const [search, setSearch] = useState(saved?.search ?? '')
  const [selected, setSelected] = useState<SelectedEntity | null>(null)
  const [collapsedFeatures, setCollapsedFeatures] = useState<Set<string>>(() =>
    typeof window !== 'undefined' ? loadCollapsedFeatures() : new Set(),
  )

  useEffect(() => {
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify({ projectId, activeOnly, search }))
    } catch {}
  }, [projectId, activeOnly, search])

  // Persist collapsed-features set whenever it changes.
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, JSON.stringify(Array.from(collapsedFeatures)))
    } catch {}
  }, [collapsedFeatures])

  const toggleFeatureCollapse = useCallback((id: string) => {
    setCollapsedFeatures((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const treeQuery = useQuery({
    queryKey: ['agile', 'tree', { projectId, activeOnly }],
    queryFn: () => {
      const params = new URLSearchParams()
      if (projectId) params.set('projectId', projectId)
      if (!activeOnly) params.set('activeOnly', 'false')
      const qs = params.toString()
      return apiClient.get<Tree>(`/agile/tree${qs ? `?${qs}` : ''}`)
    },
  })

  const projectsQuery = useQuery({
    queryKey: ['projects', { all: true }],
    queryFn: () => apiClient.get<{ results: Project[] }>('/projects?limit=200'),
  })

  const { filtered, totals } = useMemo(() => {
    if (!treeQuery.data) {
      return { filtered: [] as Feature[], totals: { features: 0, userStories: 0, tasks: 0 } }
    }
    const q = search.trim().toLowerCase()
    const features = q
      ? treeQuery.data.features
          .map((f) => filterFeature(f, q))
          .filter((f): f is Feature => f !== null)
      : treeQuery.data.features

    const totals = features.reduce(
      (acc, f) => {
        acc.features += 1
        acc.userStories += f.userStories.length
        acc.tasks += f.userStories.reduce((s, us) => s + us.tasks.length, 0)
        return acc
      },
      { features: 0, userStories: 0, tasks: 0 },
    )
    return { filtered: features, totals }
  }, [treeQuery.data, search])

  /**
   * When the user is searching, force-expand every visible feature so the
   * matching user-stories/tasks aren't hidden inside a collapsed card. The
   * persisted collapsed-set is preserved and resumes when search is cleared.
   */
  const isSearching = search.trim() !== ''
  const isCollapsed = useCallback(
    (id: string) => (isSearching ? false : collapsedFeatures.has(id)),
    [isSearching, collapsedFeatures],
  )

  const expandAll = useCallback(() => setCollapsedFeatures(new Set()), [])
  const collapseAll = useCallback(() => {
    setCollapsedFeatures(new Set(filtered.map((f) => f.id)))
  }, [filtered])

  const allCollapsed = filtered.length > 0 && filtered.every((f) => collapsedFeatures.has(f.id))
  const allExpanded = filtered.length > 0 && collapsedFeatures.size === 0

  const hasFilters = Boolean(projectId) || !activeOnly || search.trim() !== ''

  const projectsById = useMemo(() => {
    const m = new Map<string, string>()
    for (const p of projectsQuery.data?.results ?? []) m.set(p.id, p.name)
    return m
  }, [projectsQuery.data])

  function resetFilters() {
    setProjectId('')
    setActiveOnly(true)
    setSearch('')
  }

  const counts = treeQuery.data?.counts ?? { features: 0, userStories: 0, tasks: 0 }
  const headerDescription = treeQuery.data
    ? hasFilters
      ? `${totals.features} feature · ${totals.userStories} user story · ${totals.tasks} task (su ${counts.features} / ${counts.userStories} / ${counts.tasks})`
      : `${counts.features} feature · ${counts.userStories} user story · ${counts.tasks} task`
    : 'Albero Feature → User Story → Task'

  return (
    <AppLayout>
      <PageHeader title="Agile" description={headerDescription} />

      <Card className="mb-6">
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-4">
              <Label htmlFor="filter-project">Progetto</Label>
              <Select
                id="filter-project"
                value={projectId}
                onChange={(e) => setProjectId(e.currentTarget.value)}
              >
                <option value="">Tutti i progetti</option>
                {projectsQuery.data?.results.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="md:col-span-3">
              <Label htmlFor="filter-status">Stato</Label>
              <Select
                id="filter-status"
                value={activeOnly ? 'active' : 'all'}
                onChange={(e) => setActiveOnly(e.currentTarget.value === 'active')}
              >
                <option value="active">Solo attive</option>
                <option value="all">Tutte (incluse completate)</option>
              </Select>
            </div>
            <div className="md:col-span-4">
              <Label htmlFor="filter-search">Cerca</Label>
              <Input
                id="filter-search"
                placeholder="Titolo feature, user story o task…"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
              />
            </div>
            <div className="md:col-span-1">
              <Button
                variant="ghost"
                onClick={resetFilters}
                disabled={!hasFilters}
                className="w-full"
              >
                <X className="size-4" /> Reset
              </Button>
            </div>
          </div>

          {/* Bulk collapse/expand toolbar. Visible only when there are
              features to act on. Disabled during search (everything is
              force-expanded so the buttons would no-op). */}
          {filtered.length > 0 && (
            <div className="mt-3 pt-3 border-t border-zinc-800/60 flex items-center justify-end gap-2">
              <span className="text-xs text-zinc-500 mr-auto">
                {collapsedFeatures.size > 0 && !isSearching
                  ? `${collapsedFeatures.size} feature compress${
                      collapsedFeatures.size === 1 ? 'a' : 'e'
                    }`
                  : null}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={expandAll}
                disabled={allExpanded || isSearching}
                title={isSearching ? 'Disabilitato durante la ricerca' : 'Espandi tutte le feature'}
              >
                <ChevronsUpDown className="size-3.5" /> Espandi tutto
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={collapseAll}
                disabled={allCollapsed || isSearching}
                title={
                  isSearching ? 'Disabilitato durante la ricerca' : 'Comprimi tutte le feature'
                }
              >
                <ChevronsDownUp className="size-3.5" /> Comprimi tutto
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      {treeQuery.isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholder
            <Card key={i}>
              <CardBody>
                <Skeleton className="h-6 w-1/3 mb-3" />
                <Skeleton className="h-4 w-2/3" />
              </CardBody>
            </Card>
          ))}
        </div>
      ) : filtered.length > 0 ? (
        <div className="space-y-6">
          {filtered.map((feature) => {
            const agg = aggregateFeature(feature)
            const collapsed = isCollapsed(feature.id)
            return (
              <Card key={feature.id} className="relative overflow-hidden">
                {/* Left edge accent — cyan for active, violet for completed/cancelled */}
                <div
                  aria-hidden
                  className={`absolute inset-y-0 left-0 w-[3px] ${
                    feature.status === 'completed' || feature.status === 'cancelled'
                      ? 'bg-gradient-to-b from-zinc-700 to-zinc-800'
                      : 'bg-gradient-to-b from-cyan-400 via-cyan-500 to-violet-500 shadow-[0_0_12px_-2px_rgba(34,211,238,0.5)]'
                  }`}
                />
                <CardBody>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Chevron-toggle button: expands/collapses the user-stories block */}
                        <button
                          type="button"
                          onClick={() => toggleFeatureCollapse(feature.id)}
                          disabled={isSearching}
                          title={
                            isSearching
                              ? 'Collapse disabilitato durante la ricerca'
                              : collapsed
                                ? 'Espandi feature'
                                : 'Comprimi feature'
                          }
                          aria-expanded={!collapsed}
                          aria-label={collapsed ? 'Espandi feature' : 'Comprimi feature'}
                          className="p-1 -ml-1 rounded-md text-zinc-400 hover:text-cyan-300 hover:bg-zinc-800/60 transition disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-zinc-400"
                        >
                          {collapsed ? (
                            <ChevronRight className="size-5" />
                          ) : (
                            <ChevronDown className="size-5" />
                          )}
                        </button>
                        <h2
                          className="font-display text-lg font-semibold text-zinc-50 truncate tracking-tight cursor-pointer hover:text-cyan-100 transition-colors"
                          onClick={() => !isSearching && toggleFeatureCollapse(feature.id)}
                          onKeyDown={(e) => {
                            if (!isSearching && (e.key === 'Enter' || e.key === ' ')) {
                              e.preventDefault()
                              toggleFeatureCollapse(feature.id)
                            }
                          }}
                        >
                          {feature.title}
                        </h2>
                        <Badge
                          variant={
                            feature.status === 'active' || feature.status === 'in_progress'
                              ? 'brand'
                              : feature.status === 'completed'
                                ? 'success'
                                : feature.status === 'cancelled'
                                  ? 'muted'
                                  : 'info'
                          }
                        >
                          {feature.status.replace(/_/g, ' ')}
                        </Badge>
                        {feature.priority !== 'medium' && (
                          <Badge
                            variant={
                              feature.priority === 'urgent'
                                ? 'danger'
                                : feature.priority === 'high'
                                  ? 'warning'
                                  : 'muted'
                            }
                          >
                            {feature.priority}
                          </Badge>
                        )}
                        {agg.hours > 0 && (
                          <span
                            className="text-xs text-zinc-400 tabular-nums"
                            title="Somma ore stimate dei task"
                          >
                            Σ {fmtHours(agg.hours)}
                          </span>
                        )}
                        {agg.sp > 0 && (
                          <span
                            className="text-xs text-zinc-400 tabular-nums"
                            title="Somma story points delle user stories"
                          >
                            {agg.sp} SP
                          </span>
                        )}
                        <InfoButton
                          label="Dettagli feature"
                          onClick={() => setSelected({ kind: 'feature', entity: feature })}
                        />
                      </div>
                      {feature.description && (
                        <p className="text-sm text-zinc-400 mt-1">{feature.description}</p>
                      )}
                    </div>
                  </div>

                  {collapsed ? (
                    /* Compact summary shown when collapsed: gives the user
                       a sense of "what's inside" without rendering the tree. */
                    <button
                      type="button"
                      onClick={() => toggleFeatureCollapse(feature.id)}
                      className="mt-4 w-full text-left text-xs text-zinc-500 hover:text-cyan-300 transition flex items-center gap-2"
                    >
                      <ChevronRight className="size-3.5" />
                      <span>
                        {agg.usCount} user stor{agg.usCount === 1 ? 'y' : 'ies'} · {agg.taskCount}{' '}
                        task
                        {agg.hours > 0 ? ` · Σ ${fmtHours(agg.hours)}` : ''}
                        {agg.sp > 0 ? ` · ${agg.sp} SP` : ''}
                      </span>
                      <span className="text-zinc-600">— clicca per espandere</span>
                    </button>
                  ) : feature.userStories.length > 0 ? (
                    <div className="mt-5 space-y-3">
                      {feature.userStories.map((us) => (
                        <div
                          key={us.id}
                          className="rounded-lg border border-zinc-800/80 bg-zinc-950/50 transition-colors duration-150 hover:border-cyan-500/25"
                        >
                          <div className="flex items-center justify-between px-4 py-3">
                            <div className="flex items-center gap-2 min-w-0">
                              <ChevronRight className="size-4 text-cyan-500/60 shrink-0" />
                              <span className="text-sm font-medium text-zinc-200 truncate">
                                {us.title}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {toNum(us.storyPoints) > 0 && (
                                <span
                                  className="text-xs text-zinc-500 tabular-nums"
                                  title="Story points"
                                >
                                  {toNum(us.storyPoints)} SP
                                </span>
                              )}
                              <Badge variant={taskStatusVariant(us.status)}>
                                {us.status.replace(/_/g, ' ')}
                              </Badge>
                              <InfoButton
                                label="Dettagli user story"
                                onClick={() =>
                                  setSelected({ kind: 'user_story', entity: us, feature })
                                }
                              />
                            </div>
                          </div>
                          {us.tasks.length > 0 && (
                            <ul className="border-t border-zinc-800 divide-y divide-zinc-800/70">
                              {us.tasks.map((task) => (
                                <li
                                  key={task.id}
                                  className="px-4 py-2.5 flex items-center justify-between gap-4 text-sm transition-colors hover:bg-zinc-900/50"
                                >
                                  <div className="min-w-0">
                                    <div className="text-zinc-200 truncate">{task.title}</div>
                                    <div className="text-xs text-zinc-500 mt-0.5">
                                      {task.assignedAgentType ? (
                                        <span>agente · {task.assignedAgentType}</span>
                                      ) : task.assignedUserId ? (
                                        <span>utente</span>
                                      ) : (
                                        <span>non assegnato</span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    {toNum(task.estimatedHours) > 0 && (
                                      <span
                                        className="text-xs text-zinc-500 tabular-nums"
                                        title="Ore stimate"
                                      >
                                        {fmtHours(toNum(task.estimatedHours))}
                                      </span>
                                    )}
                                    <Badge variant={taskStatusVariant(task.status)}>
                                      {task.status.replace(/_/g, ' ')}
                                    </Badge>
                                    <InfoButton
                                      label="Dettagli task"
                                      onClick={() =>
                                        setSelected({
                                          kind: 'task',
                                          entity: task,
                                          feature,
                                          userStory: us,
                                        })
                                      }
                                    />
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-4 text-xs text-zinc-500">Nessuna user story ancora.</p>
                  )}
                </CardBody>
              </Card>
            )
          })}
        </div>
      ) : (
        <Card>
          <CardBody>
            <Empty
              icon={KanbanSquare}
              title={hasFilters ? 'Nessun risultato' : 'Nessuna feature'}
              description={
                hasFilters
                  ? 'Prova a modificare i filtri o resettarli.'
                  : 'Crea le feature, user story e task da Claude Code via tool MCP agile.upsertFeature.'
              }
              action={
                hasFilters ? (
                  <Button variant="ghost" onClick={resetFilters}>
                    <X className="size-4" /> Reset filtri
                  </Button>
                ) : undefined
              }
            />
          </CardBody>
        </Card>
      )}

      <AgileDetailDrawer
        entity={selected?.entity ?? null}
        kind={selected?.kind ?? 'feature'}
        context={
          selected
            ? selected.kind === 'feature'
              ? {
                  projectName: selected.entity.projectId
                    ? projectsById.get(selected.entity.projectId)
                    : undefined,
                }
              : selected.kind === 'user_story'
                ? {
                    featureTitle: selected.feature.title,
                    projectName: selected.feature.projectId
                      ? projectsById.get(selected.feature.projectId)
                      : undefined,
                  }
                : {
                    featureTitle: selected.feature.title,
                    userStoryTitle: selected.userStory.title,
                    projectName: selected.feature.projectId
                      ? projectsById.get(selected.feature.projectId)
                      : undefined,
                  }
            : undefined
        }
        onClose={() => setSelected(null)}
      />
    </AppLayout>
  )
}

function InfoButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="p-1 rounded-md text-zinc-500 hover:text-cyan-300 hover:bg-zinc-800/60 transition"
    >
      <Info className="size-3.5" />
    </button>
  )
}

function filterFeature(f: Feature, q: string): Feature | null {
  const featureMatches = f.title.toLowerCase().includes(q)
  const userStories = f.userStories
    .map((us) => {
      const usMatches = us.title.toLowerCase().includes(q)
      const tasks = usMatches ? us.tasks : us.tasks.filter((t) => t.title.toLowerCase().includes(q))
      if (usMatches || tasks.length > 0 || featureMatches) {
        return { ...us, tasks: featureMatches || usMatches ? us.tasks : tasks }
      }
      return null
    })
    .filter((us): us is UserStory => us !== null)

  if (featureMatches || userStories.length > 0) {
    return { ...f, userStories: featureMatches ? f.userStories : userStories }
  }
  return null
}
